import { useState, useEffect, useRef } from "react";
import "./PrivacyPolicyPage.css";

const API = process.env.REACT_APP_API_URL || "";

const PrivacyPolicyPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeSection, setActiveSection] = useState(0);
    const sectionsRef = useRef([]);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const res = await fetch(`${API}/api/legal/active?type=privacy`);
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load");
                setData(json.data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPolicy();
    }, []);

    // Parse content into sections by splitting on headings (lines starting with # or numbered)
    const parseSections = (content) => {
        if (!content) return [];
        const lines = content.split("\n");
        const sections = [];
        let current = null;

        lines.forEach((line) => {
            const isHeading =
                /^#{1,3}\s/.test(line) || /^\d+\.\s[A-Z]/.test(line);
            if (isHeading) {
                if (current) sections.push(current);
                current = {
                    title: line.replace(/^#{1,3}\s/, "").replace(/^\d+\.\s/, ""),
                    body: [],
                };
            } else if (current) {
                current.body.push(line);
            } else {
                sections.push({ title: "", body: [line] });
            }
        });
        if (current) sections.push(current);
        return sections.filter((s) => s.title || s.body.join("").trim());
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = sectionsRef.current.indexOf(entry.target);
                        if (idx !== -1) setActiveSection(idx);
                    }
                });
            },
            { threshold: 0.4 }
        );
        sectionsRef.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [data]);

    if (loading) {
        return (
            <div className="pp-loader">
                <span className="pp-loader__dot" />
                <span className="pp-loader__dot" />
                <span className="pp-loader__dot" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="pp-error">
                <p>{error}</p>
            </div>
        );
    }

    const sections = parseSections(data?.content);
    const updatedDate = data?.updatedAt
        ? new Date(data.updatedAt).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        : "";

    return (
        <div className="pp-root">
            {/* Background grain */}
            <div className="pp-grain" aria-hidden="true" />

            {/* Hero */}
            <header className="pp-hero">
                <div className="pp-hero__inner">
                    <span className="pp-hero__eyebrow">Legal</span>
                    <h1 className="pp-hero__title">Privacy Policy</h1>
                    {data?.version && (
                        <p className="pp-hero__meta">
                            Version {data.version} &nbsp;·&nbsp; Last updated {updatedDate}
                        </p>
                    )}
                </div>
                <div className="pp-hero__line" aria-hidden="true" />
            </header>

            <div className="pp-layout">
                {/* Sidebar ToC */}
                {sections.length > 0 && (
                    <aside className="pp-toc">
                        <p className="pp-toc__label">Contents</p>
                        <nav>
                            {sections
                                .filter((s) => s.title)
                                .map((s, i) => (
                                    <a
                                        key={i}
                                        href={`#section-${i}`}
                                        className={`pp-toc__item ${activeSection === i ? "pp-toc__item--active" : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            sectionsRef.current[i]?.scrollIntoView({
                                                behavior: "smooth",
                                            });
                                        }}
                                    >
                                        <span className="pp-toc__num">{String(i + 1).padStart(2, "0")}</span>
                                        {s.title}
                                    </a>
                                ))}
                        </nav>
                    </aside>
                )}

                {/* Main content */}
                <main className="pp-content">
                    {sections.length === 0 ? (
                        <p className="pp-empty">No content available.</p>
                    ) : (
                        sections.map((section, i) => (
                            <section
                                key={i}
                                id={`section-${i}`}
                                className="pp-section"
                                ref={(el) => (sectionsRef.current[i] = el)}
                            >
                                {section.title && (
                                    <h2 className="pp-section__title">
                                        <span className="pp-section__num">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {section.title}
                                    </h2>
                                )}
                                <div className="pp-section__body">
                                    {section.body
                                        .filter((l) => l.trim())
                                        .map((line, j) => (
                                            <p key={j}>{line}</p>
                                        ))}
                                </div>
                            </section>
                        ))
                    )}

                    <footer className="pp-footer">
                        <p>
                            If you have questions about this policy, please contact us at{" "}
                            <a href="mailto:privacy@aviatricks.in">support@aviatricks.in</a>.
                        </p>
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;